<template>
    <el-aside :style="{width: collapsed?'60px':'256px'}">
        <el-scrollbar style="height: calc(100% - 41px)">
            <aside-wrapper @click="collapsed=false">
                <slot></slot>
            </aside-wrapper>
        </el-scrollbar>
        <div class="footer">
            <div class="footer-left" @click.stop>
                <template v-if="collapsed">
                    <el-tooltip :content="versionTooltip" placement="top">
                        <el-icon style="margin: 9px 8px 9px 12px" :size="18">
                            <InfoFilled/>
                        </el-icon>
                    </el-tooltip>
                </template>
                <template v-else>
                    <el-tooltip :content="versionTooltip" placement="top">
                        <span class="version-text">
                            <span>{{ versionText }}</span>
                            <span class="backend-version-text">{{ backendVersionText }}</span>
                        </span>
                    </el-tooltip>
                </template>
            </div>
            <div class="footer-right" @click="collapsed = !collapsed">
                <el-icon style="margin: 9px 20px 9px 0" :size="22">
                    <ArrowRight v-if="collapsed"/>
                    <ArrowLeft v-else/>
                </el-icon>
            </div>
        </div>
    </el-aside>
</template>

<script>
import AsideGroup from "./AsideGroup";
import AsideWrapper from "./AsideWrapper";
import AsideItem from "./AsideItem";
import {computed} from "vue";
import {ArrowLeft, ArrowRight, InfoFilled} from '@element-plus/icons-vue'
import axios from 'ts-axios-new'

const JIGSAW_API_BASE_URL = (
    window.location.hostname === 'jigsaw-cms-test.plutodesk.com'
        ? 'https://jigsaw-api-test.plutodesk.com'
        : (import.meta.env.VITE_JIGSAW_API_BASE_URL || 'https://jigsaw-api.plutodesk.com')
).replace(/\/$/, '');

export default {
    name: "AsideNav",
    components: {AsideItem, AsideWrapper, AsideGroup, ArrowLeft, ArrowRight, InfoFilled},
    props: {data: Object},
    data() {
        return {
            collapsed: false,
            version: '',
            backendDeployment: null,
            backendDeploymentLoadFailed: false,
        }
    },
    provide() {
        return {collapsed: computed(_ =>this.collapsed), unCollapse: this.unCollapse, collapse: this.collapse}
    },
    methods: {
        collapse() {
            this.collapsed = true;
        },
        unCollapse() {
            this.collapsed = false;
        },
        loadBackendDeploymentInfo() {
            this.backendDeploymentLoadFailed = false;
            axios.get(`${JIGSAW_API_BASE_URL}/api/v1/deployment-info`, {timeout: 5000})
                .then(res => {
                    this.backendDeployment = res.data || null;
                })
                .catch(() => {
                    this.backendDeployment = null;
                    this.backendDeploymentLoadFailed = true;
                });
        },
        formatBackendStartedAt(value) {
            if (!value) return '未知';
            const date = new Date(value);
            return isNaN(date.getTime()) ? value : date.toLocaleString();
        }
    },
    computed: { 
        versionText() {
            const raw = (this.version || '').replace(/\"/g, '').replace(/"/g, '').trim();
            const ts = Number(raw);
            if (ts && !isNaN(ts)) {
                const d = new Date(ts);
                return `构建时间 ${d.toLocaleString()}`;
            }
            return raw ? `版本 ${raw}` : '开发版';
        },
        backendVersionText() {
            if (!this.backendDeployment) {
                return this.backendDeploymentLoadFailed ? '后端版本未获取' : '后端版本加载中';
            }
            return `后端 ${this.backendDeployment.releaseMarker || '未标识'}`;
        },
        versionTooltip() {
            const cms = `CMS：${this.versionText}`;
            if (!this.backendDeployment) {
                return `${cms}\n后端：${this.backendDeploymentLoadFailed ? '部署指纹接口未就绪' : '加载中'}`;
            }
            const backend = this.backendDeployment;
            return `${cms}\n后端标识：${backend.releaseMarker || '未标识'}\n后端启动：${this.formatBackendStartedAt(backend.processStartedAt)}\n后端提交：${backend.revision || '部署平台未注入'}`;
        }
    },
    mounted() {
        // 生产环境下，vite 构建时会在 dist 根目录写入 version.json（Git 提交号）
        // 开发环境可能没有该文件，回退为"开发版"
        fetch('version.json', { cache: 'no-store' })
            .then(res => res.ok ? res.text() : Promise.reject('no version'))
            .then(text => { this.version = text; })
            .catch(() => { this.version = 'dev'; });
        this.loadBackendDeploymentInfo();
    }
}
</script>

<style scoped>
.el-aside {
    transition: 0.3s ease;
}
.el-aside .footer {
    font-size: 18px;
    border-top: 1px solid var(--el-border-color);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.footer-left {
    cursor: default;
}
.footer-right {
    cursor: pointer;
}
.version-text {
    display: inline-flex;
    flex-direction: column;
    margin: 9px 12px;
    font-size: 12px;
    color: var(--el-text-color-secondary);
    white-space: nowrap;
    max-width: calc(256px - 60px);
    overflow: hidden;
    text-overflow: ellipsis;
}
.backend-version-text {
    margin-top: 2px;
}
</style>
